import { useState } from "react";
import { Sparkles, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SettingsPage } from "@/components/v2/settings-page";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";
import { useMyAccounts, useSwitchAccount } from "@/hooks/use-accounts";
import { useDeleteDemoAccount } from "@/hooks/use-demo-account";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";

function useDemoAccount() {
  const { data } = useMyAccounts();
  const accounts: {
    id: string;
    name: string;
    is_demo?: boolean;
    created_at: string;
  }[] = data?.accounts ?? [];
  return accounts.find((a) => a.is_demo === true) ?? null;
}

export default function V2SettingsExplorePage() {
  const demoAccount = useDemoAccount();
  const { data: accountData } = useMyAccounts();
  const switchAccount = useSwitchAccount();
  const deleteDemoAccount = useDeleteDemoAccount();
  const { openDialog } = useAddDemoAccountStore();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const activeAccountId = accountData?.active_account_id;
  const isDemoActive = demoAccount?.id === activeAccountId;

  return (
    <SettingsPage
      title="Explore Adva"
      subtitle="Create and manage your Adva demo account."
    >
      {demoAccount == null ? (
        <Card className="border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-start gap-3">
              <Sparkles className="size-5 mt-0.5 text-[color:var(--color-accent)] shrink-0" />
              <div>
                <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                  Explore with sample data
                </p>
                <p className="mt-1 typo-body text-[color:var(--color-text-md)]">
                  Create a separate demo account pre-loaded with Canopy Home
                  Services — two fully-populated businesses (Canopy Turf +
                  Canopy Coatings) ready to explore. Your production data is
                  unaffected.
                </p>
              </div>
            </div>
            <Button onClick={openDialog}>Create demo account</Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[color:var(--color-border)] bg-[color:var(--color-surface)]">
          <CardContent className="p-0">
            <table className="w-full typo-body">
              <thead>
                <tr className="border-b border-[color:var(--color-border)]">
                  <th className="px-4 py-2 text-left typo-label text-[color:var(--color-text-lo)] font-medium uppercase">
                    Account
                  </th>
                  <th className="px-4 py-2 text-left typo-label text-[color:var(--color-text-lo)] font-medium uppercase">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left typo-label text-[color:var(--color-text-lo)] font-medium uppercase">
                    Created
                  </th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-4 py-3 text-[color:var(--color-text-hi)]">
                    {demoAccount.name}
                  </td>
                  <td className="px-4 py-3 text-[color:var(--color-text-md)]">
                    {isDemoActive ? "Active" : "Inactive"}
                  </td>
                  <td className="px-4 py-3 text-[color:var(--color-text-md)]">
                    {demoAccount.created_at
                      ? new Date(demoAccount.created_at).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {!isDemoActive && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => switchAccount.mutate(demoAccount.id)}
                          disabled={switchAccount.isPending}
                        >
                          <ExternalLink className="mr-1.5 size-3.5" />
                          Go to demo account
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-[color:var(--color-danger)] hover:text-[color:var(--color-danger)] border-[color:var(--color-border)]"
                        onClick={() => setDeleteOpen(true)}
                      >
                        Delete demo account
                      </Button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </CardContent>

          {/* Rendered inside the demoAccount != null branch so demoAccount is
              narrowed — no non-null assertion needed. */}
          <ConfirmDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            title="Delete demo account"
            description="This will permanently delete the Canopy Home Services demo account and all its data. Your production account is unaffected."
            confirmLabel="Delete demo account"
            variant="destructive"
            pending={deleteDemoAccount.isPending}
            onConfirm={() => {
              deleteDemoAccount.mutate(demoAccount.id, {
                onSuccess: () => setDeleteOpen(false),
              });
            }}
          />
        </Card>
      )}
    </SettingsPage>
  );
}
